/**
 * coordination.types.ts - 协调成本模块类型定义
 *
 * 功能说明：
 * - 协调费用类型、状态等类型定义
 *
 * 依赖关系：
 * - 被协调成本相关页面和组件使用
 * - 依赖 project.types.ts 中的 ProjectStatus
 *
 * 关键注释索引：
 * - [协调费用] - 见下方 CoordinationCost 接口
 * - [费用类型] - 见下方 CoordinationCostType 枚举
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 */

import { ProjectStatus } from './project.types';

/**
 * [协调费用子类型] 协调费用子类型枚举
 *
 * @description 更细分的协调费用分类
 */
export enum CoordinationCostSubType {
  GENERAL = 'general',
  SITE_SUPPORT = 'site-support',
  EXTERNAL_COMMUNICATION = 'external-communication',
  TEMPORARY_EXPENSE = 'temporary-expense',
  HOSPITALITY = 'hospitality',
  GOVERNMENT_COORDINATION = 'government-coordination',
  TRAFFIC = 'traffic',
  OTHER = 'other',
}

/**
 * [协调审批状态] 协调费用审批状态枚举
 */
export enum CoordinationApprovalStatus {
  NONE = 'none',
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

/**
 * [协调来源类型] 协调费用来源类型枚举
 */
export enum CoordinationSourceType {
  MANUAL = 'manual',
  IMPORT = 'import',
  SYSTEM = 'system',
}

/**
 * [协调费用类型] 协调费用大类枚举
 *
 * @description 协调费用的主要分类
 * - MANAGEMENT: 管理费
 * - COORDINATION: 协调费
 * - MISC: 杂费
 * - EMERGENCY: 应急费用
 */
export enum CoordinationCostType {
  MANAGEMENT = 'management',
  COORDINATION = 'coordination',
  MISC = 'misc',
  EMERGENCY = 'emergency'
}

/**
 * [协调费用] 协调费用记录接口
 *
 * @description 记录项目的协调费用明细
 */
export interface CoordinationCost {
  _id: string;
  projectId: string;
  project?: {
    _id: string;
    name: string;
    projectCode?: string;
    status?: ProjectStatus;
  };
  costDate: string;
  costType: CoordinationCostType;
  subType: CoordinationCostSubType;
  approvalStatus: CoordinationApprovalStatus;
  sourceType: CoordinationSourceType;
  amount: number;
  description?: string;
  handler?: string;
  riskFlag: boolean;
  riskReason?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * [协调统计] 协调费用治理统计数据
 *
 * @description 汇总协调费用的整体情况
 */
export interface CoordinationGovernanceStats {
  totalCost: number;
  recordCount: number;
  byType: {
    management: number;
    coordination: number;
    misc: number;
    emergency: number;
  };
  bySubType: Array<{
    subType: string;
    amount: number;
    count: number;
  }>;
  riskRecords: {
    count: number;
    amount: number;
  };
}

/**
 * [创建协调费用] 创建协调费用的数据类型
 *
 * @description 用于提交新的协调费用记录
 */
export interface CreateCoordinationCostData {
  projectId: string;
  costDate?: string;
  costType: CoordinationCostType;
  subType?: CoordinationCostSubType;
  approvalStatus?: CoordinationApprovalStatus;
  sourceType?: CoordinationSourceType;
  amount: number;
  description?: string;
  handler?: string;
  riskFlag?: boolean;
  riskReason?: string;
}

/**
 * [更新协调费用] 更新协调费用的数据类型
 *
 * @description 用于修改现有协调费用记录
 */
export interface UpdateCoordinationCostData {
  projectId?: string;
  costDate?: string;
  costType?: CoordinationCostType;
  subType?: CoordinationCostSubType;
  approvalStatus?: CoordinationApprovalStatus;
  sourceType?: CoordinationSourceType;
  amount?: number;
  description?: string;
  handler?: string;
  riskFlag?: boolean;
  riskReason?: string;
}

/**
 * [协调费用查询] 协调费用查询参数
 *
 * @description 用于协调费用列表筛选
 */
export interface CoordinationCostQueryParams {
  projectId?: string;
  costType?: CoordinationCostType;
  subType?: CoordinationCostSubType;
  approvalStatus?: CoordinationApprovalStatus;
  riskFlag?: boolean;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}
